import type { DataProvider } from '@refinedev/core';
import { apiClient } from '../lib/api-client';

const stringify = (params: Record<string, unknown>): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
};

export const dataProvider: DataProvider = {
  getList: async ({ resource, pagination, filters, sorters }) => {
    const url = `/${resource}`;

    const { current = 1, pageSize = 10 } = pagination ?? {};

    const queryParams: Record<string, unknown> = {
      page: current,
      limit: pageSize,
    };

    // Add filters
    if (filters) {
      filters.forEach((filter) => {
        if ('field' in filter && filter.value !== undefined && filter.value !== '') {
          queryParams[filter.field] = filter.value;
        }
      });
    }

    // Add sorters
    if (sorters && sorters.length > 0) {
      queryParams.sortBy = sorters[0].field;
      queryParams.sortOrder = sorters[0].order;
    }

    const query = stringify(queryParams);
    const response = await apiClient.get(`${url}?${query}`);

    // Handle different response formats
    const data = response.data.data || response.data.items || response.data;
    const total = response.data.meta?.total || response.data.total || data.length;

    return {
      data,
      total,
    };
  },

  getOne: async ({ resource, id }) => {
    const url = `/${resource}/${id}`;
    const response = await apiClient.get(url);

    return {
      data: response.data.data || response.data,
    };
  },

  create: async ({ resource, variables }) => {
    const url = `/${resource}`;
    const response = await apiClient.post(url, variables);

    return {
      data: response.data.data || response.data,
    };
  },

  update: async ({ resource, id, variables }) => {
    const url = `/${resource}/${id}`;
    const response = await apiClient.put(url, variables);

    return {
      data: response.data.data || response.data,
    };
  },

  deleteOne: async ({ resource, id }) => {
    const url = `/${resource}/${id}`;
    const response = await apiClient.delete(url);

    return {
      data: response.data.data || response.data,
    };
  },

  getMany: async ({ resource, ids }) => {
    const responses = await Promise.all(
      ids.map((id) => apiClient.get(`/${resource}/${id}`))
    );

    return {
      data: responses.map((response) => response.data.data || response.data),
    };
  },

  createMany: async ({ resource, variables }) => {
    const responses = await Promise.all(
      variables.map((item) => apiClient.post(`/${resource}`, item))
    );

    return {
      data: responses.map((response) => response.data.data || response.data),
    };
  },

  updateMany: async ({ resource, ids, variables }) => {
    const responses = await Promise.all(
      ids.map((id) => apiClient.put(`/${resource}/${id}`, variables))
    );

    return {
      data: responses.map((response) => response.data.data || response.data),
    };
  },

  deleteMany: async ({ resource, ids }) => {
    const responses = await Promise.all(
      ids.map((id) => apiClient.delete(`/${resource}/${id}`))
    );

    return {
      data: responses.map((response) => response.data.data || response.data),
    };
  },

  custom: async ({ url, method, payload, query, headers }) => {
    let requestUrl = url;

    if (query) {
      requestUrl = `${url}?${stringify(query)}`;
    }

    const response = await apiClient.request({
      url: requestUrl,
      method,
      data: payload,
      headers,
    });

    return {
      data: response.data,
    };
  },

  getApiUrl: () => {
    return import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
  },
};
